import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  deleteCatalogInstance,
  fetchBucketsMetrics,
  fetchCatalogDetails,
  fetchCountObject,
  fetchSearchObject,
} from "./catalogThunks";
import { MinioCatalogDetails } from "../../api/catalogApi";
import { updateCatalogInstance } from "./Details/catalogDetailsThunks";
import { MinIOCredentials } from "../common/types";

export type comparatorItems =
  | "all"
  | "lessthan"
  | "lessequal"
  | "equal"
  | "greaterthan"
  | "greaterequal";

export interface CatBucketBucketStat {
  bucket: string;
  objects: number;
  totalSize: number;
  objectVersions: number;
}

export interface SearchObjectsResult {
  bucket: string;
  key: string;
  versionId: string;
  lastModified: string;
  etag: string;
  size: number;
  storageClass: string;
  userMetadata: any;
  isDeleteMarker: boolean;
}

export interface CatSearchObject {
  loading: boolean;
  activeSearch: boolean;
  bucketPattern: string;
  prefix: string;
  sizeLimit: string;
  sizeComparator: comparatorItems;
  sizeUnit: string;
  time: string;
  timeUnit: string;
  timeComparator: comparatorItems;
  maxItems: number;
  results: SearchObjectsResult[];
}

export interface CountTotal {
  objects: number;
  objectVersions: number;
  totalSize: number;
}

export interface BucketsTotal {
  bucket: string;
  objects: number;
  objectVersions: number;
  totalSize: number;
}

export interface CountResult {
  activeSearch: boolean;
  totals: CountTotal;
  buckets: BucketsTotal[];
}

export interface CatCountObject {
  loading: boolean;
  activeSearch: boolean;
  bucketPattern: string;
  prefix: string;
  sizeLimit: string;
  sizeComparator: comparatorItems;
  sizeUnit: string;
  time: string;
  timeUnit: string;
  timeComparator: comparatorItems;
  results: CountResult;
}

export interface DetailsSectionState {
  statsLoading: boolean;
  bucketsStats: CatBucketBucketStat[];
  searchObject: CatSearchObject;
  countObject: CatCountObject;
  details: MinioCatalogDetails | null;
  loadingCatalogDetails: boolean;
  openDeleteCatalogModal: boolean;
  openUpdateCatalogConfirmModal: boolean;
  openDeleteCatalogConfirmModal: boolean;
  loadingDeleteCatalog: boolean;
  loadingUpdateCatalog: boolean;
  catalogDeleted: boolean;
  minioCredentials?: MinIOCredentials;
  volumeSizeFactor: string;
  resourcesCPULimitError?: string;
  resourcesCPURequestError?: string;
  resourcesMemoryLimitError?: string;
  resourcesMemoryRequestError?: string;
  resourcesSpecifyLimit?: boolean;
  imageRegistry?: string;
  imageRegistryPassword?: string;
  imageRegistryUsername?: string;
  isCustomRegistry?: boolean;
  isCustomRegistryCreds?: boolean;
}

const initialState: DetailsSectionState = {
  statsLoading: true,
  bucketsStats: [],
  searchObject: {
    loading: false,
    activeSearch: false,
    bucketPattern: "",
    prefix: "",
    sizeLimit: "",
    sizeComparator: "all",
    time: "",
    timeComparator: "all",
    timeUnit: "secs",
    sizeUnit: "Mi",
    maxItems: 10000,
    results: [],
  },
  countObject: {
    loading: false,
    activeSearch: false,
    bucketPattern: "",
    prefix: "",
    sizeLimit: "",
    sizeComparator: "all",
    time: "",
    timeComparator: "all",
    timeUnit: "secs",
    sizeUnit: "Mi",
    results: {
      activeSearch: false,
      totals: {
        objects: 0,
        totalSize: 0,
        objectVersions: 0,
      },
      buckets: [],
    },
  },
  details: null,
  loadingCatalogDetails: true,
  openDeleteCatalogModal: false,
  loadingDeleteCatalog: false,
  loadingUpdateCatalog: false,
  catalogDeleted: false,
  openUpdateCatalogConfirmModal: false,
  openDeleteCatalogConfirmModal: false,
  volumeSizeFactor: "Gi",
};

export const catalogSlice = createSlice({
  name: "catalogSlice",
  initialState,
  reducers: {
    setStatsLoading: (state, action: PayloadAction<boolean>) => {
      state.statsLoading = action.payload;
    },
    setSearchLoading: (state, action: PayloadAction<boolean>) => {
      state.searchObject.loading = action.payload;
    },
    setCountLoading: (state, action: PayloadAction<boolean>) => {
      state.countObject.loading = action.payload;
    },
    resetSearch: (state) => {
      state.searchObject = initialState.searchObject;
    },
    resetCount: (state) => {
      state.countObject = initialState.countObject;
    },
    setSearchBucketPattern: (state, action: PayloadAction<string>) => {
      state.searchObject.bucketPattern = action.payload;
    },
    setSearchPrefix: (state, action: PayloadAction<string>) => {
      state.searchObject.prefix = action.payload;
    },
    setSearchSizeLimit: (state, action: PayloadAction<string>) => {
      state.searchObject.sizeLimit = action.payload;
    },
    setSearchSizeComparator: (
      state,
      action: PayloadAction<comparatorItems>,
    ) => {
      state.searchObject.sizeComparator = action.payload;
    },

    setSearchTime: (state, action: PayloadAction<string>) => {
      state.searchObject.time = action.payload;
    },
    setSearchTimeComparator: (
      state,
      action: PayloadAction<comparatorItems>,
    ) => {
      state.searchObject.timeComparator = action.payload;
    },
    setSearchSizeUnit: (state, action: PayloadAction<string>) => {
      state.searchObject.sizeUnit = action.payload;
    },
    setSearchTimeUnit: (state, action: PayloadAction<string>) => {
      state.searchObject.timeUnit = action.payload;
    },
    setCountBucketPattern: (state, action: PayloadAction<string>) => {
      state.countObject.bucketPattern = action.payload;
    },
    setCountPrefix: (state, action: PayloadAction<string>) => {
      state.countObject.prefix = action.payload;
    },
    setCountSizeLimit: (state, action: PayloadAction<string>) => {
      state.countObject.sizeLimit = action.payload;
    },
    setCountSizeComparator: (state, action: PayloadAction<comparatorItems>) => {
      state.countObject.sizeComparator = action.payload;
    },
    setCountTime: (state, action: PayloadAction<string>) => {
      state.countObject.time = action.payload;
    },
    setCatalogDetails: (state, action: PayloadAction<MinioCatalogDetails>) => {
      state.details = action.payload;
    },
    setCountTimeComparator: (state, action: PayloadAction<comparatorItems>) => {
      state.countObject.timeComparator = action.payload;
    },
    setCountSizeUnit: (state, action: PayloadAction<string>) => {
      state.countObject.sizeUnit = action.payload;
    },
    setCountTimeUnit: (state, action: PayloadAction<string>) => {
      state.countObject.timeUnit = action.payload;
    },
    setLoadingCatalogDetails: (state, action: PayloadAction<boolean>) => {
      state.loadingCatalogDetails = action.payload;
    },
    setOpenDeleteCatalog: (state, action: PayloadAction<boolean>) => {
      state.openDeleteCatalogModal = action.payload;
    },
    setCatalogDeleted: (state, action: PayloadAction<boolean>) => {
      state.catalogDeleted = action.payload;
    },
    setOpenUpdateCatalogConfirmModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.openUpdateCatalogConfirmModal = action.payload;
    },
    setCatalogMinIOCredentials: (
      state,
      action: PayloadAction<MinIOCredentials>,
    ) => {
      state.minioCredentials = action.payload;
    },
    setCatalogVolumeSizeFactor: (state, action: PayloadAction<string>) => {
      state.volumeSizeFactor = action.payload;
    },
    setOpenDeleteCatalogConfirmModal: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.openDeleteCatalogConfirmModal = action.payload;
    },
    setCatalogCPURequestError: (state, action: PayloadAction<string>) => {
      state.resourcesCPURequestError = action.payload;
    },
    setCatalogMemoryLimitError: (state, action: PayloadAction<string>) => {
      state.resourcesMemoryLimitError = action.payload;
    },
    setCatalogMemoryRequestError: (state, action: PayloadAction<string>) => {
      state.resourcesMemoryRequestError = action.payload;
    },
    setCatalogResourcesSpecifyLimit: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.resourcesSpecifyLimit = action.payload;
    },
    setCatalogIsCustomRegistry: (state, action: PayloadAction<boolean>) => {
      state.isCustomRegistry = action.payload;
    },
    setCatalogIsCustomRegistryCreds: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isCustomRegistryCreds = action.payload;
    },
    setCatalogImageRegistry: (state, action: PayloadAction<string>) => {
      state.imageRegistry = action.payload;
    },
    setCatalogImageRegistryPassword: (state, action: PayloadAction<string>) => {
      state.imageRegistryPassword = action.payload;
    },
    setCatalogImageRegistryUsername: (state, action: PayloadAction<string>) => {
      state.imageRegistryUsername = action.payload;
    },
    resetCatalog: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBucketsMetrics.pending, (state) => {
        state.statsLoading = true;
      })
      .addCase(fetchBucketsMetrics.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.bucketsStats = action.payload;
      })
      .addCase(fetchBucketsMetrics.rejected, (state, action) => {
        state.statsLoading = false;
        console.error("Bucket Metrics fetch errors:", action);
      })
      .addCase(fetchSearchObject.pending, (state) => {
        state.searchObject.loading = true;
      })
      .addCase(fetchSearchObject.fulfilled, (state, action) => {
        state.searchObject.loading = false;
        state.searchObject.activeSearch = true;
        state.searchObject.results = action.payload;
      })
      .addCase(fetchSearchObject.rejected, (state, action) => {
        state.searchObject.loading = false;
        state.searchObject.activeSearch = false;
        console.error("Search Object fetch errors:", action);
      })
      .addCase(fetchCountObject.pending, (state) => {
        state.countObject.loading = true;
      })
      .addCase(fetchCountObject.fulfilled, (state, action) => {
        state.countObject.loading = false;
        state.countObject.results = action.payload;
      })
      .addCase(fetchCountObject.rejected, (state, action) => {
        state.countObject.loading = false;
        console.error("Count Objects fetch errors:", action);
      })
      // Fetch Catalog Details
      .addCase(fetchCatalogDetails.fulfilled, (state, action) => {
        state.loadingCatalogDetails = false;
        state.details = action.payload;
      })
      .addCase(fetchCatalogDetails.pending, (state) => {
        state.loadingCatalogDetails = true;
      })
      .addCase(fetchCatalogDetails.rejected, (state) => {
        state.loadingCatalogDetails = false;
        state.details = null;
      })
      // Delete Catalog Instance
      .addCase(deleteCatalogInstance.pending, (state) => {
        state.loadingDeleteCatalog = true;
      })
      .addCase(deleteCatalogInstance.fulfilled, (state) => {
        state.loadingDeleteCatalog = false;
        state.catalogDeleted = true;
      })
      .addCase(deleteCatalogInstance.rejected, (state) => {
        state.loadingDeleteCatalog = false;
      })
      // Update Catalog Instance
      .addCase(updateCatalogInstance.pending, (state) => {
        state.loadingUpdateCatalog = true;
      })
      .addCase(updateCatalogInstance.fulfilled, (state) => {
        state.loadingUpdateCatalog = false;
      })
      .addCase(updateCatalogInstance.rejected, (state) => {
        state.loadingUpdateCatalog = false;
      });
  },
});

export const {
  setStatsLoading,
  setSearchBucketPattern,
  setSearchPrefix,
  setSearchSizeLimit,
  setSearchSizeComparator,
  setSearchTime,
  setSearchTimeComparator,
  setSearchSizeUnit,
  setSearchTimeUnit,
  setCountBucketPattern,
  setCountPrefix,
  setCountSizeLimit,
  setCountSizeComparator,
  setCountTime,
  setCountTimeComparator,
  setCountSizeUnit,
  setCountTimeUnit,
  resetSearch,
  resetCount,
  setLoadingCatalogDetails,
  setOpenDeleteCatalog,
  setCatalogDeleted,
  setOpenUpdateCatalogConfirmModal,
  setOpenDeleteCatalogConfirmModal,
  setCatalogDetails,
  setCatalogMinIOCredentials,
  setCatalogVolumeSizeFactor,
  setCatalogCPURequestError,
  setCatalogMemoryLimitError,
  setCatalogMemoryRequestError,
  setCatalogResourcesSpecifyLimit,
  setCatalogImageRegistry,
  setCatalogImageRegistryPassword,
  setCatalogImageRegistryUsername,
  setCatalogIsCustomRegistry,
  setCatalogIsCustomRegistryCreds,
  resetCatalog,
} = catalogSlice.actions;

export default catalogSlice.reducer;
